/**
 * Winners module DTOs (§12.2–12.3, §15.2): winners board, win-ID lookup, contact status marking, the tracked
 * redirect `/go/:winId/:platform` and the signed win link `/w/:winId`.
 */
import { z } from 'zod';
import { IsoDateTime, paginated, SocialHandle, Ulid } from './common.js';
import { ClaimStatus, FulfillmentStatus, PrizeTier, SocialPlatform, VerificationDecision, WinSource, WinnerContactStatus, } from './enums.js';
import { WinId, WinLinkQuery } from './ids.js';
import { MoneyDto } from './money.js';
export const WinnerBoardQuery = z.object({
    lotteryId: Ulid.optional(),
    /** `messaged=false` → "won but hasn't messaged" (§12.3). Accepts `true|false|1|0` (query strings), D-1712. */
    messaged: z
        .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true' || v === '1'))
        .optional(),
    contactStatus: WinnerContactStatus.optional(),
    platform: SocialPlatform.optional(),
    prizeId: Ulid.optional(),
    claimStatus: ClaimStatus.optional(),
    assignedTo: Ulid.optional(),
    deadlineBefore: IsoDateTime.optional(),
    sort: z.enum(['claimDeadline', 'wonAt', 'lastContactAt']).default('claimDeadline'),
});
export const WinnerBoardRowDto = z.object({
    winId: z.string(),
    winIdDisplay: z.string(),
    lotteryId: Ulid,
    lotteryTitle: z.string(),
    prize: z.object({
        prizeId: Ulid,
        name: z.string(),
        tier: PrizeTier,
        value: MoneyDto,
        sponsorName: z.string().optional(),
    }),
    source: WinSource,
    claimId: Ulid.nullable(),
    claimStatus: ClaimStatus.nullable(),
    verification: VerificationDecision.nullable(),
    fulfillmentStatus: FulfillmentStatus.nullable(),
    platform: SocialPlatform.nullable(),
    /** Masked by default (§16.3); unmasking is a separate permissioned action. */
    handleMasked: z.string().nullable(),
    displayName: z.string(),
    contactStatus: WinnerContactStatus,
    buttonTappedAt: IsoDateTime.nullable(),
    messagedAt: IsoDateTime.nullable(),
    followedUpAt: IsoDateTime.nullable(),
    assignedTo: z.object({ staffId: Ulid, name: z.string() }).nullable(),
    reminderAt: IsoDateTime.nullable(),
    claimDeadline: IsoDateTime,
    wonAt: IsoDateTime,
    notesCount: z.number().int(),
});
/** `GET /v1/admin/winners/lookup/:winId` — paste a win ID or signed link; mismatches are reported, never guessed. */
export const WinnerLookupParams = z.object({ winId: WinId });
export const WinnerLookupQuery = WinLinkQuery.partial();
export const WinnerLookupDto = z.object({
    found: z.literal(true),
    linkVerified: z.boolean().nullable(),
    winner: WinnerBoardRowDto,
    claimSummary: z.object({
        selectedVariant: z.string().nullable(),
        deliveryPreference: z.string().nullable(),
        legalNameMasked: z.string().nullable(),
        timeline: z.array(z.object({ status: ClaimStatus, at: IsoDateTime })),
    }),
    notes: z.array(z.object({ noteId: Ulid, staffId: Ulid, text: z.string(), at: IsoDateTime })),
});
/** `PATCH /v1/admin/winners/:id/contact` — MESSAGED / FOLLOWED_UP are admin actions only (Appendix A). */
export const UpdateWinnerContactRequest = z.object({
    contactStatus: z.enum(['MESSAGED', 'FOLLOWED_UP']).optional(),
    platform: SocialPlatform.optional(),
    /** New handle seen in the conversation (Appendix A: winner messages from a different handle). */
    observedHandle: z.string().max(64).optional(),
    assignTo: Ulid.nullable().optional(),
    note: z.string().max(2000).optional(),
    reminderAt: IsoDateTime.nullable().optional(),
    outcome: z.string().max(300).optional(),
});
/** `GET /v1/go/:winId/:platform` — tracked redirect to the client's own account with a pre-filled message. */
export const GoRedirectParams = z.object({ winId: WinId, platform: SocialPlatform });
export const GoRedirectDto = z.object({
    platform: SocialPlatform,
    /** Deep link (wa.me, ig.me, m.me, t.me …) with the message encoded. */
    redirectUrl: z.string(),
    /** Shown to copy if the app does not open (§12.2). */
    message: z.string(),
    accountLabel: z.string(),
});
/** `GET /v1/w/:winId` — what a signed win link resolves to (for admins opening it). */
export const WinLinkDto = z.object({
    winId: z.string(),
    winIdDisplay: z.string(),
    valid: z.boolean(),
    expired: z.boolean(),
    /** Only when valid and the caller has `winners.lookup`: full lookup; otherwise just the prize name. */
    prizeName: z.string(),
    lotteryTitle: z.string(),
    lookup: WinnerLookupDto.optional(),
});
export const WinnerExportRequest = z.object({
    lotteryId: Ulid,
    format: z.enum(['CSV', 'JSON']).default('CSV'),
    includeContacts: z.boolean().default(false),
    purpose: z.string().min(3).max(200),
});
// ---------------------------------------------------------------------------------------------------------------
// Winners module extensions (winners agent, D-1701…): supersets of the DTOs above so existing clients still parse.
// ---------------------------------------------------------------------------------------------------------------
/** Board row with the ids the console needs for actions and purpose-bound unmasking (console.md "Needs"). */
export const WinnerRowDto = WinnerBoardRowDto.extend({
    /** `WinnerContact` id — the `:id` of `PATCH /v1/admin/winners/:id/contact` (a win ID is accepted too, D-1707). */
    winnerContactId: Ulid,
    participantId: Ulid,
    channelId: Ulid.nullable(),
    reminderNote: z.string().nullable(),
    /** §12.5 separate winner-publicity consent (never implied by participation). */
    publicityConsent: z.boolean(),
});
export const WinnerBoardPageDto = paginated(WinnerRowDto);
/** `GET /v1/admin/winners/lookup/:winId` response superset: how the pasted link verified. */
export const WinnerLookupResultDto = WinnerLookupDto.extend({
    winner: WinnerRowDto,
    /** `none` (no link pasted) | `verified` | `expired` | `mismatch` (signature or win ID differs). */
    linkState: z.enum(['none', 'verified', 'expired', 'mismatch']),
});
/** `POST /v1/admin/winners/:id/handle` — Appendix A "winner messages from a different handle than entered". */
export const RecordWinnerHandleRequest = z.object({
    platform: SocialPlatform,
    handle: SocialHandle,
    note: z.string().max(500).optional(),
});
/** `GET /v1/admin/winners/export` — opted-in publicity list only (§12.5); initials and win ID, never a legal name. */
export const WinnerExportQuery = z.object({
    lotteryId: Ulid.optional(),
    format: z.enum(['CSV', 'JSON']).default('JSON'),
    purpose: z.string().max(200).optional(),
});
export const WinnerPublicityRowDto = z.object({
    winIdDisplay: z.string(),
    initials: z.string(),
    prizeName: z.string(),
    lotteryTitle: z.string(),
    wonAt: IsoDateTime,
    platform: SocialPlatform.nullable(),
});
export const WinnerExportDto = z.object({
    lotteryId: Ulid.nullable(),
    generatedAt: IsoDateTime,
    consentPurpose: z.literal('WINNER_PUBLICITY'),
    count: z.number().int(),
    items: z.array(WinnerPublicityRowDto),
});
/** `GET /v1/go/:winId/:platform` as JSON: the redirect plus what the copy fallback shows. */
export const GoRedirectResponseDto = GoRedirectDto.extend({
    winIdDisplay: z.string(),
    /** Absolute signed win link embedded in the message. */
    winLink: z.string(),
    linkExpiresAt: IsoDateTime,
    /** True when the platform deep link carries the text itself; false → "copy this message" is the primary path. */
    prefillSupported: z.boolean(),
});
/** `GET /v1/w/:winId?exp&sig` superset: what the signed win summary shows (no PII, §12.2 step 3). */
export const WinSummaryDto = WinLinkDto.extend({
    claimStatus: ClaimStatus.nullable(),
    /** Participant-safe status sentence (`copy.ts`). */
    claimStatusText: z.string(),
    claimDeadline: IsoDateTime.nullable(),
    contactStatus: WinnerContactStatus.nullable(),
    /** Reminder set on the winners board; also shown on the winner's status page (§12.3). */
    reminder: z.object({ at: IsoDateTime, note: z.string().nullable() }).nullable(),
    /** What an admin reading the link should do next. */
    adminHint: z.string(),
});
/** Signed win-link lifetime floor beyond the claim deadline (D-1702). */
export const WIN_LINK_GRACE_DAYS = 30;
export const WIN_LINK_PURPOSES = ['handoff', 'status', 'admin_lookup'];
/** Platforms whose public deep link accepts a pre-filled text parameter (§12.2 "why no approval is needed"). */
export const PREFILL_PLATFORMS = ['WHATSAPP', 'TELEGRAM', 'X', 'FACEBOOK'];
/**
 * Builds the public deep link for a preconfigured account (`LotterySocialPlatform.accountUrl`) and a message.
 * wa.me / t.me / m.me / x.com compose accept `text`; Instagram (ig.me) and TikTok open the account and the
 * message is copied (D-1705). Query strings already on the account URL are kept.
 */
export function buildPlatformDeepLink(platform, accountUrl, message) {
    const text = encodeURIComponent(message);
    const join = (base, param) => `${base}${base.includes('?') ? '&' : '?'}${param}`;
    switch (platform) {
        case 'WHATSAPP':
            return { url: join(accountUrl, `text=${text}`), prefillSupported: true };
        case 'TELEGRAM':
            return { url: join(accountUrl, `text=${text}`), prefillSupported: true };
        case 'FACEBOOK':
            return { url: join(accountUrl, `text=${text}`), prefillSupported: true };
        case 'X': {
            const handle = accountUrl
                .replace(/^https?:\/\/(www\.)?(x|twitter)\.com\//i, '')
                .replace(/[/?#].*$/, '');
            const base = handle ? `https://x.com/messages/compose?text=${text}` : join(accountUrl, `text=${text}`);
            return { url: base, prefillSupported: true };
        }
        case 'INSTAGRAM':
        case 'TIKTOK':
        case 'OTHER':
        default:
            return { url: accountUrl, prefillSupported: false };
    }
}
//# sourceMappingURL=winners.js.map